import { useGomakeAxios, useSnackBar } from "@/hooks";
import {
  getAndSetClientAddress,
  getAndSetClientContacts,
} from "@/services/hooks";
import {
  IContactData,
  addressSelectState,
  clientAddressState,
  clientContactsState,
  quoteConfirmationState,
  quoteItemState,
} from "@/store";
import { useCallback, useEffect, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { currencyUnitState } from "@/store/currency-units";
import { DOCUMENT_TYPE } from "../quotes/enums";
import { useRouter } from "next/router";
import { EHttpMethod } from "@/services/api-service/enums";
import {
  addressModalState,
  EAddressType,
} from "@/widgets/quote-new/business-widget/address-widget/state";
import {
  addDocumentAddressApi,
  getDocumentApi,
  updateDocumentAddressApi,
  updateDocumentCommentsApi,
  updateDocumentInternalNotesApi,
} from "@/services/api-service/generic-doc/documents-api";
import { consumePendingClearDocumentNotes } from "@/pages-components/board-missions/utils/duplicate-notes-selection";
import { v4 as uuidv4 } from "uuid";
import {
  getClientPaymentItemsApi,
  getReceiptByIdApi,
} from "@/services/api-service/generic-doc/receipts-api";
import { getAllShipmentTypesApi } from "@/services/api-service/shipmentTypes/shipment-type-api";
import { EPreferences } from "@/enums/prefernces";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { validateAddressApi } from "@/services/api-service/customers/customers-api";
import { applyClientAgentToDocument } from "@/widgets/quote-new/utils/default-document-agent";
import { useStartOptimisticQuickBooksTax } from "@/hooks/use-start-optimistic-quickbooks-tax";
import { useTranslation } from "react-i18next";
import { shouldReopenOrderModalState } from "@/store/order-modal-states";
import { useButtonsContainer } from "@/widgets/quote-new/buttons-container/use-buttons-container";
import { navStatusState, pageTitleState } from "@/store/nav-status";
import { useDocumentTitle } from "@/pages-components/quote-new/use-document-title";
import { userProfileState } from "@/store/user-profile";
import { printHousePreferencesState } from "@/store/print-house-preferences";
import { isUsPrintHouseState, printHouseProfile } from "@/store/print-house-profile";
import {
  buildDraftCalculateDocumentPayload,
  isDraftInvoiceQuickBooksTax,
  mergeDraftTaxCalculationResult,
} from "@/widgets/quote-new/utils/draft-invoice-tax";
import { shouldShowSystemVatMismatchWarning } from "@/widgets/quote-new/utils/quickbooks-tax-ui";
import {
  buildQuoteDocumentAddressRequiredFieldRules,
  getMissingAddressSubFieldsByPreference,
  resolveUsesUsCountryStateAddressForm,
} from "@/utils/customer-required-fields";

let activeGetQuoteRequestId = 0;

const useQuoteGetData = (
  docType?: DOCUMENT_TYPE,
  systemVAT?: number,
  setOpenVatMismatchModal?: (open: boolean) => void,
  // Child widgets call this hook too. Only the document page should cancel
  // an in-flight load on unmount, otherwise hiding the form (receipt create)
  // drops the get-by-id response and the skeleton never clears.
  cancelInFlightOnUnmount = false,
) => {
  const { callApi } = useGomakeAxios();
  const userProfile = useRecoilValue(userProfileState);
  const { t } = useTranslation();
  const router = useRouter();
  const {
    alertSuccessUpdate,
    alertSuccessAdded,
    alertFaultAdded,
    alertFaultGetData,
    alertRequiredFields,
    alertFault,
  } = useSnackBar();
  const { CheckPreferenceValue } = usePrintHousePreferences();
  const setPageTitle = useSetRecoilState(pageTitleState);
  const { documentTitle } = useDocumentTitle(
    docType,
    router?.query?.isOutgoingPayments === "true"
  );
  const isUsPrintHouse = useRecoilValue(isUsPrintHouseState);
  const isUsAddress = resolveUsesUsCountryStateAddressForm(
    isUsPrintHouse,
    !!CheckPreferenceValue(EPreferences.ADDRESSES_WITH_COUNTRY_AND_STATE),
  );
  const printHousePrefs = useRecoilValue<any>(printHousePreferencesState);
  const printHouseProfileState = useRecoilValue(printHouseProfile);
  const isNewCreation = router.query.isNewCreation === "true";
  const isDraftInvoiceQuickBooksTaxEnabled = isDraftInvoiceQuickBooksTax(
    docType,
    isNewCreation,
    printHouseProfileState?.erpType,
  );
  const { startOptimisticTaxCalculation } = useStartOptimisticQuickBooksTax();

  const alertMissingAddressSubFields = (
    item: any,
    addressType: EAddressType,
  ) => {
    const addressRules = buildQuoteDocumentAddressRequiredFieldRules(
      printHousePrefs ?? [],
      addressType,
      "customer",
    );
    const missingSubFields = getMissingAddressSubFieldsByPreference(
      item,
      addressRules,
      isUsAddress,
    );
    if (missingSubFields.length === 0) {
      return false;
    }
    const translatedFields = missingSubFields.map((key) => t(key)).join(", ");
    alertFault(`${t("modal.RequiredFields")}: ${translatedFields}`);
    return true;
  };
  const [clientAddressValue, setClientAddressValue] =
    useRecoilState<any>(clientAddressState);
  const setClientContactsValue =
    useSetRecoilState<IContactData[]>(clientContactsState);
  const currenciesUnits = useRecoilValue<any>(currencyUnitState);
  const addressSelect = useRecoilValue(addressSelectState);
  const quoteConfirm = useRecoilValue<any>(quoteConfirmationState);
  const [shipmentTypes, setShipmentTypes] = useState<
    { label: string; value: string; price: any }[]
  >([]);
  const [quoteItemValue, setQuoteItemValue] =
    useRecoilState<any>(quoteItemState);
  const setOpenModal = useSetRecoilState<boolean>(addressModalState);
  const [requestAbortController, setRequestAbortController] =
    useState<AbortController>(null);

  const recalculateDraftInvoiceAfterAddress = async (nextQuoteItemValue: any) => {
    if (!isDraftInvoiceQuickBooksTaxEnabled) {
      setQuoteItemValue(nextQuoteItemValue);
      return;
    }

    startOptimisticTaxCalculation();
    const res = await callApi(
      EHttpMethod.POST,
      `/v1/erp-service/documents/calculate-document-new`,
      {
        documentType: docType,
        document: buildDraftCalculateDocumentPayload(nextQuoteItemValue),
      },
      false,
    );

    if (res?.success) {
      setQuoteItemValue(
        mergeDraftTaxCalculationResult(
          nextQuoteItemValue,
          res?.data?.data?.data,
        ),
      );
      return;
    }

    setQuoteItemValue(nextQuoteItemValue);
  };

  const getAllClientContacts = useCallback(async () => {
    if (quoteItemValue?.customerID) {
      await getAndSetClientContacts(callApi, setClientContactsValue, {
        ClientId: quoteItemValue?.customerID,
      });
    }
  }, [quoteItemValue?.customerID, quoteConfirm]);

  const getAllClientAddress = useCallback(async () => {
    if (quoteItemValue?.customerID) {
      return await getAndSetClientAddress(callApi, setClientAddressValue, {
        ClientId: quoteItemValue?.customerID,
      });
    }
  }, [quoteItemValue?.customerID]);

  const getAllShipmentTypes = async (includeSelfPickup = false) => {
    const callBack = (res) => {
      if (res?.success) {
        let types = res?.data.map((item) => ({
          value: item.id,
          label: item.title,
          price: item?.initialPrice,
        }));

        // Add "Self Pickup" only if the prop is true
        if (includeSelfPickup) {
          types.push({
            value: "self_pickup",
            label: "Self Pickup",
            price: 0, // Assuming there's no price for self-pickup
          });
        }

        setShipmentTypes(types);
      }
    };

    await getAllShipmentTypesApi(callApi, callBack);
  };

  const getCurrencyUnitText = (currency) => {
    const foundCurrency = currenciesUnits?.find((c) => c.value === currency);
    if (foundCurrency) {
      return foundCurrency.text;
    } else {
      return "";
    }
  };

  const { onClickOpenOrderNowModal } = useButtonsContainer(DOCUMENT_TYPE.quote);
  const [shouldReopen, setShouldReopen] = useRecoilState(
    shouldReopenOrderModalState
  );

  useEffect(() => {
    if (!cancelInFlightOnUnmount) {
      return;
    }
    return () => {
      activeGetQuoteRequestId += 1;
    };
  }, [cancelInFlightOnUnmount]);

  const getQuote = useCallback(async () => {
    if (!router.isReady) {
      return;
    }

    const requestId = ++activeGetQuoteRequestId;
    const isCurrentRequest = () => requestId === activeGetQuoteRequestId;
    if (requestAbortController) {
      requestAbortController.abort();
    }

    try {
      if (
        router?.query?.isNewCreation &&
        docType === DOCUMENT_TYPE.receipt &&
        router?.query?.documentNumber &&
        router?.query?.ClientId
      ) {
        return; // No need to call anything if opening receipt page from pay option
      }

      if (router?.query?.isNewCreation && docType === DOCUMENT_TYPE.receipt) {
        const callBack = (res) => {
          if (!isCurrentRequest()) {
            return;
          }
          if (res?.success) {
            const _data = res?.data || {};
            setQuoteItemValue(_data);
          } else {
            alertFaultGetData();
          }
        };
        await getClientPaymentItemsApi(callApi, callBack);
      } else if (router?.query?.isNewCreation) {
        const requestBody = {
          documentType: docType,
          ...(router?.query.orderId && { orderId: router.query.orderId }),
          ...(router?.query.deliveryNoteId && {
            deliveryNoteID: router.query.deliveryNoteId,
          }),
          ...(router?.query.proformaInvoiceId && {
            proformaInvoiceId: router.query.proformaInvoiceId,
          }),
          ...(router?.query.documentToDuplicateId && {
            documentToDuplicateId: router.query.documentToDuplicateId,
          }),
          ...(router?.query.documentId && {
            documentId: router.query.documentId,
          }),
          ...(router?.query.budgetId && { budgetId: router.query.budgetId }),

          ...(router?.query.draftInvoiceId && {
            documentId: router.query.draftInvoiceId,
          }),
          ...(router?.query.draftPurchaseInvoiceId && {
            documentId: router.query.draftPurchaseInvoiceId,
          }),
        };

        const res = await callApi(
          EHttpMethod.POST,
          `/v1/erp-service/documents/get-new-document-data`,
          requestBody
        );

        // A newer load replaced this one. Closing an order writes the
        // idempotency key into the URL and used to start a second fetch;
        // the first response is not a failure.
        if (!isCurrentRequest()) {
          return;
        }

        if (res?.success) {
          const _data = res?.data?.data?.result || {};
          applyClientAgentToDocument(
            _data,
            _data?.client?.agentId || _data?.client?.AgentId,
          );
          // Set default language to creator's language if not set
          if (!_data?.defaultLanguage && userProfile?.systemLang) {
            _data.defaultLanguage = userProfile.systemLang;
          }
          setQuoteItemValue(_data);
          // Check if the client vat Exempt and show modal if needed
          if (systemVAT && setOpenVatMismatchModal && _data?.client) {
            const isNewCreation = router?.query?.isNewCreation === "true";
            const isCorrectPage =
              router.pathname === "/invoice" ||
              router.pathname === "/deliveryNote";
            const isVATMismatch = Math.abs(systemVAT - _data?.vat) > 0.001;

            if (
              isNewCreation &&
              isCorrectPage &&
              isVATMismatch &&
              shouldShowSystemVatMismatchWarning(
                printHouseProfileState?.erpType,
                router.pathname,
              )
            ) {
              setOpenVatMismatchModal(true);
            }
          }
        } else if (res?.data?.message === "source_document_currency_mismatch") {
          // The source document is in another system currency / has an invalid rate.
          alertFault(res.data.message);
        } else {
          alertFaultGetData();
        }
      } else if (docType === DOCUMENT_TYPE.receipt) {
        const isOutgoingPayments = router?.query?.isOutgoingPayments === "true";
        const supplierPaymentId = router?.query?.id;

        const callBack = (res) => {
          if (!isCurrentRequest()) {
            return;
          }
          if (res?.success) {
            const _data = res?.data || {};
            setPageTitle(documentTitle + " " + _data.number);
            setQuoteItemValue(_data);
          } else {
            alertFaultGetData();
          }
        };
        await getReceiptByIdApi(callApi, callBack, {
          receiptId: router?.query?.Id || router?.query?.id,
        });
      } else {
        const newRequestAbortController = new AbortController();
        setRequestAbortController(newRequestAbortController);

        const callBack = async (res) => {
          if (!isCurrentRequest()) {
            return;
          }
          if (res?.success) {
            let indexs = 0;
            let _data = res?.data || {};
            // Handle defaultLanguage from response
            if (_data?.defaultLanguage) {
              _data.defaultLanguage = _data.defaultLanguage;
            }

            // "Duplicate without notes" → clear quote document Notes / Internal notes
            // (WriteCommentComp) that were copied onto the new quote cart.
            // Quote-only + document-scoped pending flag (see duplicate-notes-selection).
            if (
              docType === DOCUMENT_TYPE.quote &&
              _data?.id &&
              consumePendingClearDocumentNotes(_data.id, {
                // Unscoped pending only on cart quote (/quote with no Id)
                allowUnscoped: !router?.query?.Id,
              }) &&
              (String(_data?.notes ?? "").trim() ||
                String(_data?.internalNotes ?? "").trim())
            ) {
              await updateDocumentCommentsApi(callApi, () => { }, {
                documentType: docType,
                contact: {
                  documentId: _data.id,
                  comments: "",
                },
              });
              await updateDocumentInternalNotesApi(callApi, () => { }, {
                documentType: docType,
                contact: {
                  documentId: _data.id,
                  internalNotes: "",
                },
              });
              _data = { ..._data, notes: "", internalNotes: "" };
            }

            setPageTitle(documentTitle + " " + _data.number);
            const mapData = _data?.documentItems?.map((item, index) => {
              indexs++;
              const parentIndex = indexs;
              const _childsDocumentItemsMapping =
                item?.childsDocumentItems?.map((child, index2) => {
                  indexs++;
                  return {
                    id: indexs,
                    amount: child?.quantity,
                    unitPrice: child?.price,
                    discount: child?.discount,
                    finalPrice: child?.finalPrice,
                    quoteItemId: child?.id,
                  };
                });
              return {
                id: parentIndex,
                itemName: item?.productName,
                details: (
                  <div
                    style={
                      _childsDocumentItemsMapping
                        ? {
                          height: "100%",
                          overflowY: "auto",
                          paddingRight: 5,
                        }
                        : { height: 36, overflowY: "auto", paddingRight: 5 }
                    }
                  >
                    {item?.content}
                  </div>
                ),
                amount: item?.quantity,
                unitPrice: item?.price,
                discount: item?.discount,
                finalPrice: item?.finalPrice,
                quoteItemId: item?.id,
                childsDocumentItems: _childsDocumentItemsMapping,
              };
            });
            _data.documentItemsMapping = mapData;
            setQuoteItemValue(_data);
            if (shouldReopen) {
              setShouldReopen(false);
              onClickOpenOrderNowModal(false, _data);
            }
          } else {
            alertFaultGetData();
          }
        };

        await getDocumentApi(
          callApi,
          callBack,
          {
            documentType: docType,
            Id: router?.query?.Id || router?.query?.id,
          },
          false,
          newRequestAbortController
        );
      }
    } catch (error) {
      if (error?.name === "AbortError" || !isCurrentRequest()) {
        return;
      }
      alertFaultGetData();
    }
  }, [
    callApi,
    docType,
    requestAbortController,
    router,
    setQuoteItemValue,
    // executeIfNeeded,
    shouldReopen,
    setShouldReopen,
  ]);

  const validateAddress = async (addressWithMetadata) => {
    return await validateAddressApi(callApi, () => { }, addressWithMetadata);
  };

  const onClickAddAddress = async (item: any, addressType: EAddressType) => {
    if (alertMissingAddressSubFields(item, addressType)) {
      return;
    }

    const addressWithMetadata = {
      ...item,
      type: addressType,
      notes: item?.notes || "",
      documentID: quoteItemValue?.id,
    };

    if (router.query.isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      const addressArray =
        addressType === EAddressType.BILLING
          ? "documentBillingAddresses"
          : "documentDeliveryAddresses";

      if (!Array.isArray(updatedQuoteItemValue[addressArray])) {
        updatedQuoteItemValue[addressArray] = [];
      }

      const newAddress = {
        id: uuidv4(),
        ...addressWithMetadata,
      };

      updatedQuoteItemValue[addressArray] = [
        ...updatedQuoteItemValue[addressArray],
        newAddress,
      ];

      await recalculateDraftInvoiceAfterAddress(updatedQuoteItemValue);
      setOpenModal(false);
    } else {
      startOptimisticTaxCalculation();
      const callBack = (res) => {
        if (res.success) {
          alertSuccessAdded();
          getQuote();
          setOpenModal(false);
        } else {
          alertFaultAdded();
        }
      };

      await addDocumentAddressApi(callApi, callBack, {
        documentType: docType,
        address: addressWithMetadata,
      });
    }
  };

  const onClickAddNewAddress = useCallback(
    async (item: any, isUpdate: boolean, addressType: EAddressType) => {
      if (alertMissingAddressSubFields(item, addressType)) {
        return;
      }

      const addressWithMetadata = Object.fromEntries(
        Object.entries({
          ...item,
          type: addressType,
          clientId: quoteItemValue?.customerID,
        }).filter(
          ([_, value]) => value !== undefined && value !== null && value !== ""
        )
      );

      if (isUsAddress) {
        const validateRes = await validateAddress(addressWithMetadata);
        if (!validateRes?.success || validateRes?.data?.ErrorMessage) {
          alertFault(
            validateRes?.data?.ErrorMessage || "Address validation failed."
          );
          return;
        }
      }

      const res = await callApi(
        EHttpMethod.POST,
        `/v1/crm-service/customer/create-address`,
        addressWithMetadata
      );

      if (res?.success) {
        alertSuccessAdded();
        const result = await getAllClientAddress();
        const addedAddress = result.find(
          (item) => item.id === res.data.data.result
        );

        if (isUpdate) {
          updateClientAddress(addedAddress, addressType);
        } else {
          onClickAddAddress(addedAddress, addressType);
        }
      } else {
        alertFaultAdded();
      }
    },
    [quoteItemValue, router, printHousePrefs, isUsAddress, t]
  );

  const updateClientAddress = async (item: any, addressType: EAddressType) => {
    if (alertMissingAddressSubFields(item, addressType)) {
      return;
    }

    const addressArray =
      addressType === EAddressType.BILLING
        ? "documentBillingAddresses"
        : "documentDeliveryAddresses";

    const addressWithMetadata = Object.fromEntries(
      Object.entries({
        ...item,
        id: quoteItemValue?.[addressArray]?.[0]?.id,
        addressID: quoteItemValue?.[addressArray]?.[0]?.addressID,
        type: addressType,
        documentID: quoteItemValue?.id,
      }).filter(
        ([_, value]) => value !== undefined && value !== null && value !== ""
      )
    );

    if (isUsAddress) {
      const validateRes = await validateAddress(addressWithMetadata);

      if (!validateRes?.success || validateRes?.data?.ErrorMessage) {
        alertFault(
          validateRes?.data?.ErrorMessage || t("errors.addressValidationFailed")
        );
        return;
      }
    }
    if (router.query.isNewCreation) {
      const updatedQuoteItemValue = { ...quoteItemValue };
      const updatedAddresses = updatedQuoteItemValue[addressArray].map(
        (address) => {
          if (address.id === item.id) {
            const cleanedItem = Object.fromEntries(
              Object.entries({
                ...address,
                ...item,
                notes: item.notes || "",
              }).filter(
                ([_, value]) =>
                  value !== null && value !== undefined && value !== ""
              )
            );
            return cleanedItem;
          }
          return address;
        }
      );

      updatedQuoteItemValue[addressArray] = updatedAddresses;
      await recalculateDraftInvoiceAfterAddress(updatedQuoteItemValue);
      setOpenModal(false);
    } else {
      startOptimisticTaxCalculation();
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();
          getQuote();
          setOpenModal(false);
        } else {
          alertFaultAdded();
        }
      };

      const addressArray =
        addressType === EAddressType.BILLING
          ? "documentBillingAddresses"
          : "documentDeliveryAddresses";

      const addressWithMetadata = Object.fromEntries(
        Object.entries({
          ...item,
          id: quoteItemValue?.[addressArray]?.[0]?.id,
          addressID: quoteItemValue?.[addressArray]?.[0]?.addressID,
          type: addressType,
          documentID: quoteItemValue?.id,
        }).filter(
          ([_, value]) => value !== undefined && value !== null && value !== ""
        )
      );

      await updateDocumentAddressApi(callApi, callBack, {
        documentType: docType,
        address: addressWithMetadata,
      });
    }
  };

  return {
    getQuote,
    quoteItemValue,
    clientAddressValue,
    addressSelect,
    getAllClientAddress,
    getAllClientContacts,
    getCurrencyUnitText,
    getAllShipmentTypes,
    shipmentTypes,
    updateClientAddress,
    onClickAddAddress,
    onClickAddNewAddress,
  };
};

export { useQuoteGetData };
